import React, { useEffect, useState } from "react";

import { Power } from "lucide-react";

import KeepUploadsCell, { DEFAULT_KEEP_DAYS } from "./KeepUploadsCell";
import PlanRefreshCell, { DEFAULT_REFRESH_DAYS } from "./PlanRefreshCell";
import QueueSlotsPanel from "./QueueSlotsPanel";
import RoleAccessPanel from "./RoleAccessPanel";
import { A } from "./strings";
import Button from "../../Components/Button";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  AccessChange,
  fetchAccountPlanAccess,
  OrgAccess,
  runRefreshCheck,
  setAccountPlanAccess,
  setAccountPlanChat,
  setQueueSlots,
  setRefreshDays,
  setRoleAccess,
  setUnusedUploadDays,
} from "../../Store/Reducers/AccountPlanAccessSlice";
import { smallButton } from "../AdminAccountPlansTemplateContainer/parts";

const formatWhen = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : "";

/** The organization's chat choice as set here (kept even while Account Plans is off). */
const chatChoice = (org: OrgAccess): boolean => org.chat_setting ?? org.chat;

/** The newer of the two switches' last changes, with which one it was. */
const latestChange = (
  org: OrgAccess
): { what: Switch; change: AccessChange } | null => {
  const plans = org.last_change;
  const chat = org.chat_last_change ?? null;
  if (!plans && !chat) return null;
  if (!chat) return { what: "plans", change: plans as AccessChange };
  if (!plans) return { what: "chat", change: chat };
  return (chat.at || "") > (plans.at || "")
    ? { what: "chat", change: chat }
    : { what: "plans", change: plans };
};

interface SwitchButtonProps {
  on: boolean;
  label: string;
  disabled: boolean;
  onClick: () => void;
}

/** An on/off switch that says what it is set to; clicking asks to confirm the change. */
const SwitchButton: React.FC<SwitchButtonProps> = ({
  on,
  label,
  disabled,
  onClick,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="inline-flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 disabled:cursor-not-allowed disabled:opacity-50"
  >
    <span
      aria-hidden
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
        on ? "bg-emerald-500" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
          on ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </span>
    <span
      className={`text-sm font-medium ${
        on ? "text-emerald-700" : "text-gray-500"
      }`}
    >
      {on ? A.on : A.off}
    </span>
  </button>
);

/** Which switch a row is confirming: Account Plans itself, or its plan chat. */
type Switch = "plans" | "chat";

interface ConfirmProps {
  org: OrgAccess;
  what: Switch;
  busy: boolean;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

/** Inline confirm for one organization (no browser dialogs). */
const Confirm: React.FC<ConfirmProps> = ({
  org,
  what,
  busy,
  onConfirm,
  onCancel,
}) => {
  const [note, setNote] = useState("");
  const chat = what === "chat";
  const turningOn = chat ? !chatChoice(org) : !org.enabled;
  const noteId = `access-note-${org.schema}${chat ? "-chat" : ""}`;
  return (
    <div
      role="group"
      aria-label={A.confirmGroup(org.name)}
      data-testid={`confirm-${what}-${org.schema}`}
      className={`rounded-md border px-3 py-2.5 text-sm ${
        turningOn
          ? "border-emerald-200 bg-emerald-50/60"
          : "border-amber-200 bg-amber-50/60"
      }`}
    >
      <p className="text-gray-800">
        {chat
          ? turningOn
            ? A.chatConfirmOn(org.name, org.enabled)
            : A.chatConfirmOff(org.name)
          : turningOn
            ? A.confirmOn(org.name, chatChoice(org))
            : A.confirmOff(org.name, org.plan_versions)}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label htmlFor={noteId} className="sr-only">
          {A.noteLabel}
        </label>
        <input
          id={noteId}
          type="text"
          value={note}
          maxLength={500}
          placeholder={A.notePlaceholder}
          onChange={e => setNote(e.target.value)}
          className="min-w-[220px] flex-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
        />
        <Button
          variant={turningOn ? "primary" : "outline-primary"}
          loading={busy}
          disabled={busy}
          onClick={() => onConfirm(note)}
        >
          {chat
            ? turningOn
              ? A.chatConfirmOnButton(org.name)
              : A.chatConfirmOffButton(org.name)
            : turningOn
              ? A.confirmOnButton(org.name)
              : A.confirmOffButton(org.name)}
        </Button>
        <button
          type="button"
          className={smallButton}
          disabled={busy}
          onClick={onCancel}
        >
          {A.cancel}
        </button>
      </div>
    </div>
  );
};

interface RefreshConfirmProps {
  org: OrgAccess;
  from: number;
  to: number;
  busy: boolean;
  /** Which number setting is confirmed: plan refresh (default) or keep unused uploads. */
  setting?: "refresh" | "keep";
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

/** Inline confirm for a new plan refresh interval; names the setting and the organization. */
const RefreshConfirm: React.FC<RefreshConfirmProps> = ({
  org,
  from,
  to,
  busy,
  setting = "refresh",
  onConfirm,
  onCancel,
}) => {
  const [note, setNote] = useState("");
  const noteId = `access-note-${org.schema}-${setting}`;
  const keep = setting === "keep";
  return (
    <div
      role="group"
      aria-label={A.confirmGroup(org.name)}
      data-testid={`confirm-${setting}-${org.schema}`}
      className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2.5 text-sm"
    >
      <p className="text-gray-800">
        {keep
          ? A.keepConfirm(org.name, from, to)
          : A.refreshConfirm(org.name, from, to)}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label htmlFor={noteId} className="sr-only">
          {A.noteLabel}
        </label>
        <input
          id={noteId}
          type="text"
          value={note}
          maxLength={500}
          placeholder={A.notePlaceholder}
          onChange={e => setNote(e.target.value)}
          className="min-w-[220px] flex-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
        />
        <Button
          variant="primary"
          loading={busy}
          disabled={busy}
          onClick={() => onConfirm(note)}
        >
          {keep
            ? A.keepConfirmButton(org.name, to)
            : A.refreshConfirmButton(org.name, to)}
        </Button>
        <button
          type="button"
          className={smallButton}
          disabled={busy}
          onClick={onCancel}
        >
          {A.cancel}
        </button>
      </div>
    </div>
  );
};

const refreshDaysOf = (org: OrgAccess): number =>
  org.refresh_days ?? DEFAULT_REFRESH_DAYS;

const keepDaysOf = (org: OrgAccess): number =>
  org.unused_upload_days ?? DEFAULT_KEEP_DAYS;

const AdminAccountPlansAccessContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { overview, loading, loadError, saving, refreshChecks, checking } =
    useAppSelector(state => state.accountPlanAccess);
  // A picked-but-unconfirmed plan refresh interval for one organization.
  const [refreshPick, setRefreshPick] = useState<{
    schema: string;
    days: number;
  } | null>(null);
  // A picked-but-unconfirmed keep period for unused uploads (failure audit Q3).
  const [keepPick, setKeepPick] = useState<{
    schema: string;
    days: number;
  } | null>(null);
  // The organization whose role access table is open (2026-11-03).
  const [rolesOpen, setRolesOpen] = useState<string | null>(null);
  // The row being confirmed: schema for Account Plans, schema + ":chat" for its chat.
  const [confirming, setConfirming] = useState<string | null>(null);

  useEffect(() => {
    void dispatch(fetchAccountPlanAccess());
  }, [dispatch]);

  const confirm = async (org: OrgAccess, what: Switch, note: string) => {
    const arg = { schema: org.schema, name: org.name, note };
    const result =
      what === "chat"
        ? await dispatch(
            setAccountPlanChat({ ...arg, enabled: !chatChoice(org) })
          )
        : await dispatch(
            setAccountPlanAccess({ ...arg, enabled: !org.enabled })
          );
    if (
      setAccountPlanAccess.fulfilled.match(result) ||
      setAccountPlanChat.fulfilled.match(result)
    )
      setConfirming(null);
  };

  const confirmRefresh = async (org: OrgAccess, days: number, note: string) => {
    const result = await dispatch(
      setRefreshDays({ schema: org.schema, name: org.name, days, note })
    );
    if (setRefreshDays.fulfilled.match(result)) setRefreshPick(null);
  };

  const confirmKeep = async (org: OrgAccess, days: number, note: string) => {
    const result = await dispatch(
      setUnusedUploadDays({ schema: org.schema, name: org.name, days, note })
    );
    if (setUnusedUploadDays.fulfilled.match(result)) setKeepPick(null);
  };

  return (
    <section aria-labelledby="ap-access-title" className="p-4 md:p-6">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
          <Power size={20} />
        </div>
        <div className="min-w-0">
          <h2
            id="ap-access-title"
            className="text-lg font-semibold text-gray-900"
          >
            {A.title}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            {A.subtitle(overview?.cache_seconds ?? 60)}
          </p>
        </div>
      </div>

      {overview?.kill_switch.engaged && (
        <div
          role="status"
          className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          {overview.kill_switch.env ? A.killEnv : A.killRuntime}
        </div>
      )}

      {overview?.queue && (
        <QueueSlotsPanel
          queue={overview.queue}
          saving={saving === "queue-slots"}
          onSave={(read, brief, note) =>
            void dispatch(setQueueSlots({ read, brief, note }))
          }
        />
      )}

      {loading && !overview && (
        <p className="text-sm text-gray-500">{A.loading}</p>
      )}
      {loadError && !overview && (
        <div className="flex items-center gap-3 text-sm text-rose-700">
          <span>{loadError}</span>
          <button
            type="button"
            className={smallButton}
            onClick={() => void dispatch(fetchAccountPlanAccess())}
          >
            {A.retry}
          </button>
        </div>
      )}

      {overview && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {A.colOrg}
                </th>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {A.colStatus}
                  <div className="mt-0.5 font-normal normal-case tracking-normal text-gray-400">
                    {A.colStatusHint}
                  </div>
                </th>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {A.colChat}
                  <div className="mt-0.5 font-normal normal-case tracking-normal text-gray-400">
                    {A.colChatHint}
                  </div>
                </th>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {A.colRefresh}
                  <div className="mt-0.5 font-normal normal-case tracking-normal text-gray-400">
                    {A.colRefreshHint}
                  </div>
                </th>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {A.colLastChange}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {overview.organizations.map(org => {
                const last = latestChange(org);
                return (
                  <React.Fragment key={org.schema}>
                    <tr
                      data-testid={`access-row-${org.schema}`}
                      className="align-top"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {org.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {A.planVersions(org.plan_versions)}
                          {!org.is_active && ` · ${A.inactive}`}
                        </div>
                        <button
                          type="button"
                          aria-expanded={rolesOpen === org.schema}
                          aria-controls={`role-access-${org.schema}`}
                          onClick={() =>
                            setRolesOpen(
                              rolesOpen === org.schema ? null : org.schema
                            )
                          }
                          className="mt-1.5 text-xs font-semibold text-[#8538d4] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                        >
                          {rolesOpen === org.schema
                            ? "Hide role access"
                            : `Role access (${(org.roles || []).length} roles)`}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <SwitchButton
                          on={org.enabled}
                          label={A.plansSwitch(org.name)}
                          disabled={saving !== null}
                          onClick={() => {
                            setRefreshPick(null);
                            setKeepPick(null);
                            setConfirming(org.schema);
                          }}
                        />
                        {org.setting === null && (
                          <div className="mt-1 text-xs text-gray-500">
                            {A.followsDefault}
                          </div>
                        )}
                      </td>
                      <td
                        className="px-4 py-3"
                        data-testid={`chat-${org.schema}`}
                      >
                        <SwitchButton
                          on={org.chat}
                          label={A.chatSwitch(org.name)}
                          disabled={saving !== null || !org.enabled}
                          onClick={() => {
                            setRefreshPick(null);
                            setKeepPick(null);
                            setConfirming(`${org.schema}:chat`);
                          }}
                        />
                        {!org.enabled && (
                          <div className="mt-1 text-xs text-gray-500">
                            {A.chatNeedsPlans}
                          </div>
                        )}
                      </td>
                      <td
                        className="px-4 py-3"
                        data-testid={`refresh-${org.schema}`}
                      >
                        <PlanRefreshCell
                          orgName={org.name}
                          schema={org.schema}
                          days={refreshDaysOf(org)}
                          disabled={saving !== null}
                          checking={checking.includes(org.schema)}
                          result={refreshChecks[org.schema]}
                          picked={
                            refreshPick?.schema === org.schema
                              ? refreshPick.days
                              : null
                          }
                          onPick={days => {
                            setConfirming(null);
                            setKeepPick(null);
                            // Back to the saved value: nothing to confirm.
                            setRefreshPick(
                              days === refreshDaysOf(org)
                                ? null
                                : { schema: org.schema, days }
                            );
                          }}
                          onCheck={() =>
                            void dispatch(
                              runRefreshCheck({
                                schema: org.schema,
                                name: org.name,
                              })
                            )
                          }
                        />
                        <div className="mt-3">
                          <KeepUploadsCell
                            orgName={org.name}
                            schema={org.schema}
                            days={keepDaysOf(org)}
                            disabled={saving !== null}
                            picked={
                              keepPick?.schema === org.schema
                                ? keepPick.days
                                : null
                            }
                            onPick={days => {
                              setConfirming(null);
                              setRefreshPick(null);
                              setKeepPick(
                                days === keepDaysOf(org)
                                  ? null
                                  : { schema: org.schema, days }
                              );
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {last ? (
                          <>
                            <div>
                              {A.lastChange(
                                last.what,
                                last.change.enabled,
                                last.change.by,
                                formatWhen(last.change.at)
                              )}
                            </div>
                            {last.change.note && (
                              <div className="mt-0.5 italic text-gray-500">
                                {last.change.note}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">
                            {A.neverChanged}
                          </span>
                        )}
                      </td>
                    </tr>
                    {rolesOpen === org.schema && (
                      <tr id={`role-access-${org.schema}`}>
                        <td colSpan={5} className="px-4 pb-4">
                          <RoleAccessPanel
                            org={org}
                            savingRole={
                              saving?.startsWith(`${org.schema}:role:`)
                                ? saving
                                : null
                            }
                            onSave={async (role, level, note) =>
                              setRoleAccess.fulfilled.match(
                                await dispatch(
                                  setRoleAccess({
                                    schema: org.schema,
                                    name: org.name,
                                    role: role.role,
                                    label: role.label,
                                    level,
                                    note,
                                  })
                                )
                              )
                            }
                          />
                        </td>
                      </tr>
                    )}
                    {(confirming === org.schema ||
                      confirming === `${org.schema}:chat`) && (
                      <tr>
                        <td colSpan={5} className="px-4 pb-3">
                          <Confirm
                            key={confirming}
                            org={org}
                            what={confirming === org.schema ? "plans" : "chat"}
                            busy={saving === confirming}
                            onConfirm={note =>
                              void confirm(
                                org,
                                confirming === org.schema ? "plans" : "chat",
                                note
                              )
                            }
                            onCancel={() => setConfirming(null)}
                          />
                        </td>
                      </tr>
                    )}
                    {refreshPick?.schema === org.schema && (
                      <tr>
                        <td colSpan={5} className="px-4 pb-3">
                          <RefreshConfirm
                            key={refreshPick.days}
                            org={org}
                            from={refreshDaysOf(org)}
                            to={refreshPick.days}
                            busy={saving === `${org.schema}:refresh`}
                            onConfirm={note =>
                              void confirmRefresh(org, refreshPick.days, note)
                            }
                            onCancel={() => setRefreshPick(null)}
                          />
                        </td>
                      </tr>
                    )}
                    {keepPick?.schema === org.schema && (
                      <tr>
                        <td colSpan={5} className="px-4 pb-3">
                          <RefreshConfirm
                            key={keepPick.days}
                            org={org}
                            setting="keep"
                            from={keepDaysOf(org)}
                            to={keepPick.days}
                            busy={saving === `${org.schema}:keep`}
                            onConfirm={note =>
                              void confirmKeep(org, keepPick.days, note)
                            }
                            onCancel={() => setKeepPick(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default AdminAccountPlansAccessContainer;
